import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { DisclosurePanel } from '@/components/molecules/Disclosure';

import type { AuditFilterBindings } from './filterBindings';
import ContentFilters from './panel/ContentFilters';
import RequestFilters from './panel/RequestFilters';
import WhatFilters from './panel/WhatFilters';
import WhenWhoFilters from './panel/WhenWhoFilters';
import WhereResultFilters from './panel/WhereResultFilters';

import styles from './AllFiltersPanel.module.scss';

interface AllFiltersPanelProps {
    id: string;
    isOpen: boolean;
    onClose: () => void;
    bindings: AuditFilterBindings;
}

/**
 * Every filter, grouped the way an investigation asks: when, who, what, where, with what
 * result, from which request, and what changed. It opens inline above the table rather
 * than over it, so the rows stay in sight while the filters change them; that is also why
 * it has no title line of its own, since the pressed "All filters" button right above
 * already names it.
 */
export default function AllFiltersPanel({ id, isOpen, onClose, bindings }: AllFiltersPanelProps) {
    const { t } = useTranslation();

    return (
        <DisclosurePanel id={id} isOpen={isOpen}>
            <section className={styles.panel} aria-labelledby={`${id}-title`}>
                <h2 id={`${id}-title`} className={styles.visuallyHidden}>
                    {t('auditLog.filters.allFilters')}
                </h2>
                <div className={styles.close}>
                    <Button variant="secondary" size="small" onClick={onClose}>
                        {t('auditLog.filters.close')}
                    </Button>
                </div>
                {/* Mounted only while open, so a closed panel keeps no second copy of the toolbar's fields. */}
                {isOpen && (
                    <div className={styles.groups}>
                        <div className={styles.half}>
                            <div className={styles.whenWho}>
                                <WhenWhoFilters bindings={bindings} />
                            </div>
                            <div className={styles.what}>
                                <WhatFilters bindings={bindings} />
                            </div>
                        </div>
                        <div className={styles.half}>
                            <div className={styles.whereResult}>
                                <WhereResultFilters bindings={bindings} />
                            </div>
                            <div className={styles.requestContent}>
                                <RequestFilters bindings={bindings} />
                                <ContentFilters bindings={bindings} />
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </DisclosurePanel>
    );
}
